export interface GeoPoint {
  lng: number;
  lat: number;
}

export type ChargingStationSource = "loading" | "amap" | "demo";

export interface ChargingStation {
  id: string;
  name: string;
  shortName: string;
  address: string;
  location: GeoPoint;
  distanceM: number;
  availableStalls: number;
  pricePerKwh: number;
  parkingFee: string;
  routeMinutes: number;
  routeDistanceKm: number;
  detourKm: number;
  tag?: string;
  source: Exclude<ChargingStationSource, "loading">;
}

export const AMAP_CHARGING_DESTINATION = {
  name: "陆家嘴中心",
  subtitle: "本次导航目的地",
  location: {
    lng: 121.49848,
    lat: 31.24728
  }
} as const;

export const AMAP_CHARGING_VEHICLE_START = {
  name: "当前车辆位置",
  location: {
    lng: 121.48688,
    lat: 31.23762
  }
} as const;

export const FALLBACK_CHARGING_STATIONS: ChargingStation[] = [
  {
    id: "zeekr-supercharge",
    name: "目的地北侧快充站",
    shortName: "北侧快充站",
    address: "目的地旁",
    location: {
      lng: 121.49872,
      lat: 31.24708
    },
    distanceM: 30,
    availableStalls: 6,
    pricePerKwh: 1.2,
    parkingFee: "10元/小时",
    routeMinutes: 15,
    routeDistanceKm: 5.6,
    detourKm: 0.4,
    tag: "推荐",
    source: "demo"
  },
  {
    id: "teld-charging",
    name: "目的地东侧快充站",
    shortName: "东侧快充站",
    address: "目的地东侧停车场",
    location: {
      lng: 121.50184,
      lat: 31.24842
    },
    distanceM: 420,
    availableStalls: 4,
    pricePerKwh: 1.26,
    parkingFee: "12元/小时",
    routeMinutes: 17,
    routeDistanceKm: 5.9,
    detourKm: 0.8,
    source: "demo"
  },
  {
    id: "starcharge",
    name: "目的地西侧快充站",
    shortName: "西侧快充站",
    address: "目的地西侧商业地库",
    location: {
      lng: 121.49292,
      lat: 31.24616
    },
    distanceM: 760,
    availableStalls: 8,
    pricePerKwh: 0.98,
    parkingFee: "6元/小时",
    routeMinutes: 19,
    routeDistanceKm: 6.4,
    detourKm: 1.2,
    source: "demo"
  }
];

export function createFallbackChargingStations(
  destinationLocation: GeoPoint = AMAP_CHARGING_DESTINATION.location
) {
  const deltaLng = destinationLocation.lng - AMAP_CHARGING_DESTINATION.location.lng;
  const deltaLat = destinationLocation.lat - AMAP_CHARGING_DESTINATION.location.lat;

  return rankChargingStations(
    FALLBACK_CHARGING_STATIONS.map((station) => ({
      ...station,
      location: {
        lng: station.location.lng + deltaLng,
        lat: station.location.lat + deltaLat
      }
    }))
  );
}

export function formatStationDistance(distanceM: number) {
  if (distanceM < 1000) {
    return `${Math.round(distanceM)}m`;
  }

  return `${(distanceM / 1000).toFixed(1)}km`;
}

export function getStationDisplayName(station: ChargingStation) {
  return station.shortName || station.name;
}

export function getRecommendedStation(stations: ChargingStation[]) {
  return stations.find((station) => station.tag === "推荐") ?? stations[0] ?? FALLBACK_CHARGING_STATIONS[0];
}

export function updateChargingStationRouteFacts(
  station: ChargingStation,
  vehicleLocation: GeoPoint,
  destinationLocation: GeoPoint = AMAP_CHARGING_DESTINATION.location
): ChargingStation {
  const vehicleToStationM = getApproxDistanceM(vehicleLocation, station.location);
  const vehicleToDestinationM = getApproxDistanceM(vehicleLocation, destinationLocation);
  const stationToDestinationM = getApproxDistanceM(station.location, destinationLocation);
  const routeDistanceKm = Math.max(0.4, vehicleToStationM / 1000 * 1.18);
  const directDestinationKm = Math.max(0.4, vehicleToDestinationM / 1000 * 1.15);
  const viaDestinationKm = routeDistanceKm + stationToDestinationM / 1000 * 1.15;

  return {
    ...station,
    routeDistanceKm,
    routeMinutes: estimateChargingRouteMinutes(routeDistanceKm),
    detourKm: Math.max(0.1, Math.min(12, viaDestinationKm - directDestinationKm))
  };
}

export function rankChargingStations(stations: ChargingStation[]) {
  return [...stations]
    .sort((a, b) => getStationScore(b) - getStationScore(a) || a.distanceM - b.distanceM)
    .map((station, index) => ({
      ...station,
      tag: index === 0 ? "推荐" : undefined
    }));
}

function getStationScore(station: ChargingStation) {
  const distanceScore = Math.max(0, 1400 - station.distanceM) / 1400 * 42;
  const availabilityScore = Math.min(station.availableStalls, 10) * 4;
  const priceScore = Math.max(0, 1.5 - station.pricePerKwh) * 36;
  const detourScore = Math.max(0, 1.6 - station.detourKm) * 10;

  return distanceScore + availabilityScore + priceScore + detourScore;
}

function estimateChargingRouteMinutes(routeDistanceKm: number) {
  return Math.max(4, Math.round(routeDistanceKm / 24 * 60));
}

function getApproxDistanceM(from: GeoPoint, to: GeoPoint) {
  const earthRadiusM = 6371000;
  const fromLat = toRadians(from.lat);
  const toLat = toRadians(to.lat);
  const deltaLat = toRadians(to.lat - from.lat);
  const deltaLng = toRadians(to.lng - from.lng);
  const haversine =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(fromLat) * Math.cos(toLat) * Math.sin(deltaLng / 2) ** 2;

  return Math.round(earthRadiusM * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine)));
}

function toRadians(value: number) {
  return value * Math.PI / 180;
}
